import React, { useMemo, useState } from 'react';
import { Card, Select, Spin } from 'antd';
import { useMerchantAnalytics } from '../../hooks/useMerchantAnalytics';
import {
  MerchantRevenueChart,
  MerchantOrdersChart,
} from './MerchantAnalyticsCharts';
import { formatVnd } from '../../utils/formatVnd';

interface AnalyticsTabProps {
  merchantId: string | undefined;
}

const RANGE_LABELS = {
  '7d': '7 ngày qua',
  '30d': '30 ngày qua',
} as const;

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ merchantId }) => {
  const [range, setRange] = useState<'7d' | '30d'>('7d');
  const { stats, statsLoading, charts, chartsLoading } = useMerchantAnalytics(
    merchantId,
    { range }
  );

  const rangeLabel = RANGE_LABELS[range];

  const chartSummary = useMemo(() => {
    if (!charts.length) return null;
    const totalOrders = charts.reduce((sum, row) => sum + (row.orders || 0), 0);
    const totalRevenue = charts.reduce(
      (sum, row) => sum + (row.revenue || 0),
      0
    );
    const peakDay = charts.reduce(
      (best, row) => ((row.revenue || 0) > (best.revenue || 0) ? row : best),
      charts[0]
    );
    return { totalOrders, totalRevenue, peakDay };
  }, [charts]);

  const formatStat = (value: number | null | undefined) => {
    if (value == null) return '–';
    return value.toLocaleString('vi-VN');
  };

  const formatGrowth = (value: number | null | undefined) => {
    if (value == null) return '–';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}% so với kỳ trước`;
  };

  return (
    <div className="merchant-detail-analytics-tab">
      <div className="merchant-detail-analytics-header">
        <Select
          value={range}
          onChange={v => setRange(v)}
          options={[
            { value: '7d', label: '7 ngày qua' },
            { value: '30d', label: '30 ngày qua' },
          ]}
          style={{ width: 140 }}
        />
        {chartSummary ? (
          <span className="merchant-detail-analytics-header-note">
            {rangeLabel}: {formatStat(chartSummary.totalOrders)} đơn ·{' '}
            {formatVnd(chartSummary.totalRevenue)}
          </span>
        ) : null}
      </div>
      <div className="merchant-detail-analytics-cards">
        <Card className="merchant-detail-analytics-card">
          <div className="merchant-detail-analytics-card-label">
            Đơn hàng ({rangeLabel})
          </div>
          <div className="merchant-detail-analytics-card-value">
            {statsLoading ? (
              <Spin size="small" />
            ) : (
              formatStat(stats?.total_orders)
            )}
          </div>
        </Card>
        <Card className="merchant-detail-analytics-card">
          <div className="merchant-detail-analytics-card-label">
            Doanh thu ({rangeLabel})
          </div>
          <div className="merchant-detail-analytics-card-value">
            {statsLoading ? (
              <Spin size="small" />
            ) : stats?.period_revenue != null ? (
              formatVnd(stats.period_revenue)
            ) : (
              '–'
            )}
          </div>
        </Card>
        <Card className="merchant-detail-analytics-card">
          <div className="merchant-detail-analytics-card-label">
            Giá trị TB/đơn
          </div>
          <div className="merchant-detail-analytics-card-value">
            {statsLoading ? (
              <Spin size="small" />
            ) : stats?.avg_order_value != null ? (
              formatVnd(stats.avg_order_value)
            ) : (
              '–'
            )}
          </div>
        </Card>
        <Card className="merchant-detail-analytics-card">
          <div className="merchant-detail-analytics-card-label">
            Tăng trưởng
          </div>
          <div className="merchant-detail-analytics-card-value merchant-detail-analytics-growth">
            {statsLoading ? (
              <Spin size="small" />
            ) : (
              formatGrowth(stats?.growth_percent)
            )}
          </div>
        </Card>
      </div>
      <div className="merchant-detail-analytics-charts">
        <MerchantRevenueChart
          data={charts}
          loading={chartsLoading}
          rangeLabel={rangeLabel}
        />
        <MerchantOrdersChart
          data={charts}
          loading={chartsLoading}
          rangeLabel={rangeLabel}
        />
      </div>
    </div>
  );
};
